import { useEffect, useState, useRef } from 'react';
import styles from './Configurations.module.css';
import { getConfigurationsService } from '../../services/getConfigurations.service';
import { Loading } from '../Loading';
import { fileUploadService, FileUploadProgress } from '../../services/fileUpload.service';

interface Configuration {
    id: string;
    key: string;
    value: string;
}


// Define asset names as a const assertion for proper typing
const assests = {
    "girl1": {
        name: "login image1",
        id: "girl1",
    },
    "girl2": {
        name: "login image2",
        id: "girl2",
    },
    "girl3": {
        name: "login image3",
        id: "girl3",
    },
    "girl4": {
        name: "login image4",
        id: "girl4",
    },
    "girl5": {
        name: "login image5",
        id: "girl5",
    },
    "girl6": {
        name: "login image6",
        id: "girl6",
    },
    "girl7": {
        name: "login image7",
        id: "girl7",
    },
    "loginBg": {
        name: "login background image",
        id: "loginBg",
    },
    "normalBg": {
        name: "normal background image",
        id: "normalBg",
    },
    "macauSwipe": {
        name: "swipe to macau image",
        id: "macauSwipe",
    },
    "macauBg": {
        name: "macau background image",
        id: "macauBg",
    },
    "normalSwipe": {
        name: "swipe to normal image",
        id: "normalSwipe",
    },
    "splashScreen": {
        name: "splash screen video",
        id: "splashScreen",
    },
    "poster": {
        name: "poster video",
        id: "poster",
    },
    "macauVideo": {
        name: "macau screen video",
        id: "macauVideo",
    },
    "normalButton": {
        name: "navigate to normal button image",
        id: "normalButton",
    },
    "macauButton": {
        name: "navigate to macau button image",
        id: "macauButton",
    },
    "normalVideo": {
        name: "normal screen video",
        id: "normalVideo",
    },
    "macauButtonBg": {
        name: "macau button background image",
        id: "macauButtonBg",
    },
    "ckLoginBg": {
        name: "CashKings login background image",
        id: "ckLoginBg",
    },
    "ckLobbyBg": {
        name: "CashKings lobby background image",
        id: "ckLobbyBg",
    },
    "ckFishLobbyBg": {
        name: "CashKings fishing lobby background image",
        id: "ckFishLobbyBg",
    },
 } as const;

// Create a type from the assets array
type AssetName = keyof typeof assests;

// Type the assetsType object properly
const assetsType: Record<AssetName, string> = {
    "girl1": ".webp,.gif,.png,.jpg,.jpeg",
    "girl2": ".webp,.gif,.png,.jpg,.jpeg",
    "girl3": ".webp,.gif,.png,.jpg,.jpeg",
    "girl4": ".webp,.gif,.png,.jpg,.jpeg",
    "girl5": ".webp,.gif,.png,.jpg,.jpeg",
    "girl6": ".webp,.gif,.png,.jpg,.jpeg",
    "girl7": ".webp,.gif,.png,.jpg,.jpeg",
    "loginBg": ".webp,.gif,.png,.jpg,.jpeg",
    "normalBg": ".webp,.gif,.png,.jpg,.jpeg",
    "macauSwipe": ".webp,.gif,.png,.jpg,.jpeg",
    "macauBg": ".webp,.gif,.png,.jpg,.jpeg",
    "normalSwipe": ".webp,.gif,.png,.jpg,.jpeg",
    "splashScreen": ".mp4",
    "poster": ".mp4",
    "macauVideo": ".mp4",
    "normalButton": ".webp,.gif,.png,.jpg,.jpeg",
    "macauButton": ".webp,.gif,.png,.jpg,.jpeg",
    "normalVideo": ".mp4",
    "macauButtonBg": ".webp,.gif,.png,.jpg,.jpeg",
    "ckLoginBg": ".webp,.png,.jpg,.jpeg",
    "ckLobbyBg": ".webp,.png,.jpg,.jpeg",
    "ckFishLobbyBg": ".webp,.png,.jpg,.jpeg",
};

const Configurations = () => {
    const [configurations, setConfigurations] = useState<Configuration[]>([]);
    const [loading, setLoading] = useState(true);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [editValue, setEditValue] = useState<string>('');
    const [updating, setUpdating] = useState<string | null>(null);
    const [feedback, setFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const [assetName, setAssetName] = useState<AssetName | ''>('');
    console.log({ assetName });
    // File upload states
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<FileUploadProgress | null>(null);
    // Shown under the upload buttons: the page-level message sits at the top,
    // out of view of the upload card.
    const [uploadFeedback, setUploadFeedback] = useState<{ type: 'success' | 'error', message: string } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        loadConfigurations();
    }, []);

    const loadConfigurations = async () => {
        try {
            setLoading(true);
            const response = await getConfigurationsService.getConfigurations();
            console.log({ response });
            setConfigurations(response || []);
        } catch (error) {
            console.error(error);
            setFeedback({ type: 'error', message: 'Failed to load configurations' });
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (config: Configuration) => {
        setEditingId(config.id);
        setEditValue(config.value);
        setFeedback(null);
    };

    const handleCancel = () => {
        setEditingId(null);
        setEditValue('');
        setFeedback(null);
    };

    const handleSave = async (id: string) => {
        if (editValue.trim() === '') {
            setFeedback({ type: 'error', message: 'Value cannot be empty' });
            return;
        }

        try {
            setUpdating(id);
            await getConfigurationsService.updateConfigurationById(parseInt(id), editValue.trim());

            // Update local state
            setConfigurations(configs =>
                configs.map(config =>
                    config.id === id
                        ? { ...config, value: editValue.trim() }
                        : config
                )
            );

            setEditingId(null);
            setEditValue('');
            setFeedback({ type: 'success', message: 'Configuration updated successfully' });

            // Clear success message after 3 seconds
            setTimeout(() => setFeedback(null), 3000);
        } catch (error) {
            console.error('Update error:', error);
            setFeedback({ type: 'error', message: 'Failed to update configuration' });
        } finally {
            setUpdating(null);
        }
    };

    // File upload handlers
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const validation = fileUploadService.validateFile(file);
        if (!validation.isValid) {
            setUploadFeedback({ type: 'error', message: validation.error || 'Invalid file' });
            return;
        }

        setSelectedFile(file);
        setUploadFeedback(null);
    };

    const handleUpload = async () => {
        if (!selectedFile || !assetName) {
            setUploadFeedback({ 
                type: 'error', 
                message: 'Please select a file and asset name' 
            });
            return;
        }

        try {
            setUploading(true);
            setUploadProgress({ loaded: 0, total: 0, percentage: 0 });

            const result = await fileUploadService.uploadFile(
                selectedFile,
                assetName,
                (progress) => setUploadProgress(progress)
            );

            if (result.success) {
                setUploadFeedback({ 
                    type: 'success', 
                    message: `${selectedFile.name} uploaded successfully as ${assetName}` 
                });
                setSelectedFile(null);
                setUploadProgress(null);
                // Reset file input
                if (fileInputRef.current) {
                    fileInputRef.current.value = '';
                }
            } else {
                setUploadFeedback({ 
                    type: 'error', 
                    message: result.message || 'Upload failed' 
                });
            }
        } catch (error) {
            console.error('Upload error:', error);
            setUploadFeedback({ 
                type: 'error', 
                message: 'Upload failed: ' + (error instanceof Error ? error.message : 'Unknown error')
            });
        } finally {
            setUploading(false);
            setUploadProgress(null);
        }
    };

    const handleCancelUpload = () => {
        setSelectedFile(null);
        setUploadProgress(null);
        setUploadFeedback(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    if (loading) {
        return (
            <div className={styles.main}>
                <div className={styles.loadingContainer}>
                    <Loading
                        type="spinner"
                        size="large"
                        text="Loading configurations..."
                        fullScreen
                    />
                </div>
            </div>
        );
    }

    return (
        <main className={styles.main}>
            <div className={styles.container}>
                <div className={styles.header}>
                    <h1 className={styles.title}>System Configurations</h1>
                    <p className={styles.subtitle}>Manage application settings and configurations</p>
                </div>

                {feedback && (
                    <div className={`${styles.feedback} ${styles[feedback.type]}`}>
                        <span className={styles.feedbackIcon}>
                            {feedback.type === 'success' ? '✅' : '❌'}
                        </span>
                        {feedback.message}
                    </div>
                )}

                <div className={styles.configurationsGrid}>
                    {configurations.map((config) => (
                        <div key={config.id} className={styles.configCard}>
                            <div className={styles.configHeader}>
                                <h3 className={styles.configKey}>{config.key}</h3>
                                <span className={styles.configId}>ID: {config.id}</span>
                            </div>

                            <div className={styles.configValue}>
                                {editingId === config.id ? (
                                    <div className={styles.editContainer}>
                                        <textarea
                                            value={editValue}
                                            onChange={(e) => setEditValue(e.target.value)}
                                            className={styles.editInput}
                                            placeholder="Enter new value..."
                                            rows={3}
                                        />
                                        <div className={styles.editActions}>
                                            <button
                                                onClick={() => handleSave(config.id)}
                                                disabled={updating === config.id}
                                                className={`${styles.button} ${styles.saveButton}`}
                                            >
                                                {updating === config.id ? 'Saving...' : 'Save'}
                                            </button>
                                            <button
                                                onClick={handleCancel}
                                                disabled={updating === config.id}
                                                className={`${styles.button} ${styles.cancelButton}`}
                                            >
                                                Cancel
                                            </button>
                                        </div>
                                    </div>
                                ) : (
                                    <div className={styles.valueContainer}>
                                        <div className={styles.valueText} title={config.value}>
                                            {config.value}
                                        </div>
                                        <button
                                            onClick={() => handleEdit(config)}
                                            className={`${styles.button} ${styles.editButton}`}
                                        >
                                            Edit
                                        </button>
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                {configurations.length === 0 && !loading && (
                    <div className={styles.emptyState}>
                        <p>No configurations found</p>
                    </div>
                )}
                <div className={styles.configCard} style={{marginTop: '1.5rem'}}>
                    <div className={styles.configHeader}>
                        <h3 className={styles.configKey}>File Upload</h3>
                        <span className={styles.configId}>{assetName ? assetsType[assetName] : "Upload assets (.webp, .mp3, .mp4, .gif only)"}</span>
                    </div>
                    
                    <div className={styles.uploadContainer}>
                        <div className={styles.uploadRow}>
                            <label htmlFor="asset-select" className={styles.selectLabel}>
                                Asset name:
                            </label>
                            <select
                                id="asset-select"
                                className={styles.providerSelect}
                                value={assetName}
                                onChange={(e) => setAssetName(e.target.value as AssetName | '')}
                                disabled={uploading}
                            >
                                <option value="">Select asset name</option>
                                {Object.keys(assests).map((asset) => (
                                    <option key={assests[asset as AssetName].id} value={assests[asset as AssetName].id}>
                                        {assests[asset as AssetName].name}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.uploadRow}>
                            <label htmlFor="file-input" className={styles.selectLabel}>
                                Select file:
                            </label>
                            <input
                                id="file-input"
                                ref={fileInputRef}
                                type="file"
                                accept={assetName ? assetsType[assetName as AssetName] : ''}
                                onChange={handleFileSelect}
                                disabled={uploading}
                                className={styles.fileInput}
                            />
                        </div>

                        {selectedFile && (
                            <div className={styles.filePreview}>
                                <div className={styles.fileInfo}>
                                    <span className={styles.fileIcon}>
                                        {fileUploadService.getFileTypeIcon(selectedFile.name)}
                                    </span>
                                    <div className={styles.fileDetails}>
                                        <div className={styles.fileName}>{selectedFile.name}</div>
                                        <div className={styles.fileSize}>
                                            {fileUploadService.formatFileSize(selectedFile.size)}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {uploadProgress && (
                            <div className={styles.progressContainer}>
                                <div className={styles.progressBar}>
                                    <div 
                                        className={styles.progressFill} 
                                        style={{ width: `${uploadProgress.percentage}%` }}
                                    ></div>
                                </div>
                                <span className={styles.progressText}>
                                    {uploadProgress.percentage}% ({fileUploadService.formatFileSize(uploadProgress.loaded)} / {fileUploadService.formatFileSize(uploadProgress.total)})
                                </span>
                            </div>
                        )}

                        <div className={styles.uploadActions}>
                            <button
                                onClick={handleUpload}
                                disabled={!selectedFile || !assetName || uploading}
                                className={`${styles.button} ${styles.uploadButton}`}
                            >
                                {uploading ? 'Uploading...' : 'Upload File'}
                            </button>
                            {selectedFile && (
                                <button
                                    onClick={handleCancelUpload}
                                    disabled={uploading}
                                    className={`${styles.button} ${styles.cancelButton}`}
                                >
                                    Cancel
                                </button>
                            )}
                        </div>

                        {uploadFeedback && (
                            <div className={`${styles.feedback} ${styles[uploadFeedback.type]}`}>
                                <span className={styles.feedbackIcon}>
                                    {uploadFeedback.type === 'success' ? '✅' : '❌'}
                                </span>
                                {uploadFeedback.message}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </main>
    );
};

export default Configurations;
